import { logger } from '@tunasong/models'
import { AgentChat, resolveOrAttachAgent } from '@tunasong/plugin-agent/integration'
import type { LayoutComponent } from '@tunasong/plugin-lib'
import { entitiesApi, useStore, useThunkDispatch } from '@tunasong/redux'
import type { Agent, Entity, Persisted } from '@tunasong/schemas'
import { LinearProgress, Typography } from '@tunasong/ui-lib'
import { useEffect, useEffectEvent, useState } from 'react'
import type { FC } from 'react'
import styles from './ai-chat.module.css'

/**
 * AI advisor layout: a standard agent thread with the `songwriting-advisor`
 * agent find-or-created on the VIEWED entity (not profile-scoped — a
 * documented deviation from a per-user advisor). Replaces the legacy bespoke
 * chatStream implementation — streaming, markdown, Stop, identity, and
 * reconciliation all come from the shared `AgentChat` surface.
 */
export const AIChatView: FC<LayoutComponent<Entity>> = props => {
  const { entity } = props
  const dispatch = useThunkDispatch()
  const { getState } = useStore()
  const [agent, setAgent] = useState<Persisted<Agent> | null>(null)
  const [error, setError] = useState(false)

  // Effect Event: reads the latest `entity` (needed whole, for ACL-inheriting
  // `parent`), `dispatch`, and `getState` without making them reactive. The
  // resolve effect below should only re-run when we navigate to a *different*
  // entity (entity.id changes) — not on every edit to the current one, since
  // Slate/RTK updates give `entity` a new object identity on every keystroke
  // and re-running resolveOrAttachAgent (and its findAgents fetch) on each of
  // those would be wasteful.
  const resolveAgent = useEffectEvent(() =>
    resolveOrAttachAgent({
      parentId: entity.id,
      definitionId: 'songwriting-advisor',
      parent: entity,
      createEntity: ({ parent, entity: agentEntity }) =>
        dispatch(entitiesApi.endpoints.createEntity.initiate({ parent, entity: agentEntity })).unwrap(),
      dispatch,
      getState,
    })
  )

  useEffect(() => {
    // Reset stale state on every (re-)run — including navigation to a
    // different entity — so song A's advisor (or its error) never renders
    // while song B's resolve is still in flight.
    let cancelled = false
    setAgent(null)
    setError(false)
    resolveAgent()
      .then(resolved => {
        if (!cancelled) {
          setAgent(resolved)
        }
      })
      .catch((resolveError: unknown) => {
        logger.error('Failed to resolve the songwriting-advisor agent', resolveError)
        if (!cancelled) {
          setError(true)
        }
      })
    return () => {
      cancelled = true
    }
  }, [entity.id])

  if (error) {
    return <Typography>Couldn't load the advisor — reload to retry.</Typography>
  }

  return agent ? (
    <div className={styles.container}>
      <AgentChat element={agent} showTabs={false} composerId="ai-chat-layout-composer" />
    </div>
  ) : (
    <LinearProgress />
  )
}

export default AIChatView
